import { addDaysToDate, diffInDays, normalizeToLocalDate, todayLocal } from './dateUtils';

export type VaccinationStatus = 'completed' | 'overdue' | 'approaching' | 'upcoming';

/** Yaklaşan sayılacak gün penceresi. */
export const APPROACHING_DAYS = 30;

interface VaccinationLike {
  id?: string;
  pet_id?: string;
  petId?: string;
  name?: string;
  vaccine_name?: string;
  date: string;
  next_due_date?: string | null;
  nextDueDate?: string | null;
  status?: string | null;
}

/** Yapılmış aşı mı? status boş/null ise completed sayılır (servis katmanıyla aynı). */
export const isDone = (v: { status?: string | null }): boolean => !v.status || v.status === 'completed';

const petOf = (v: VaccinationLike) => v.pet_id || v.petId || '';
// Aşı adını karşılaştırma için sadeleştir: büyük/küçük harf (Türkçe I/İ/ı dahil), aksan ve fazla boşluk önemsiz
export const nameOf = (v: VaccinationLike) =>
  (v.vaccine_name || v.name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/\s+/g, ' ')
    .trim();

/** Aşının sonraki doz tarihi (yerel 'YYYY-MM-DD'), yoksa ''. */
export const getDueDate = (v: VaccinationLike): string => {
  const raw = (v.next_due_date || v.nextDueDate || '').trim();
  return raw ? normalizeToLocalDate(raw) : '';
};

/** Takip edilen tarih: yapılmış aşıda sonraki doz (yoksa ''), yapılmamış aşıda aşının kendi tarihi. */
export const trackedDate = (v: VaccinationLike): string => {
  const raw = isDone(v) ? getDueDate(v) : normalizeToLocalDate(v.date || '');
  // Geçersiz/bozuk tarih ("abc", "31.12.2026") takip dışı bırakılır: NaN gün ve "Invalid Date" üretmesin
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : '';
};

/**
 * Bu kaydın sonraki dozu, aynı hayvana aynı aşıdan daha yeni bir kayıt girilerek
 * karşılanmış mı? Karşılanmışsa eski kayıt sonsuza dek "gecikmiş" kalmasın.
 */
const isSuperseded = (v: VaccinationLike, all: VaccinationLike[]): boolean => {
  const pet = petOf(v);
  const name = nameOf(v);
  if (!pet || !name) return false;
  const myDate = normalizeToLocalDate(v.date);
  // Yalnızca YAPILMIŞ (completed) daha yeni bir kayıt sonraki dozu karşılar;
  // gelecek için planlanmış (pending) kayıt eski gecikmiş uyarıyı gizlemez.
  return all.some(
    o =>
      o !== v &&
      petOf(o) === pet &&
      nameOf(o) === name &&
      isDone(o) &&
      normalizeToLocalDate(o.date) > myDate
  );
};

/**
 * Tek doğru durum fonksiyonu. Takvim günleri YEREL tarihle ve string olarak
 * karşılaştırılır (UTC kayması ve "bugün = gecikmiş" hatası olmaz).
 *
 * - Sonraki doz tarihi (next_due_date) varsa asıl takip tarihi odur; kayıt
 *   "completed" olsa bile sonraki doz gecikebilir.
 * - Sonraki doz sonradan yapıldıysa (daha yeni aynı aşı kaydı) eski kayıt tamamlanmıştır.
 */
export const getVaccinationStatus = (
  v: VaccinationLike,
  all: VaccinationLike[] = [],
  today: string = todayLocal()
): VaccinationStatus => {
  if (isSuperseded(v, all)) return 'completed';

  // Yapılmamış (planlı) aşıda takip tarihi kendi tarihidir; sonraki doz ancak aşı yapılınca geçerli olur
  const ref = trackedDate(v);

  if (!ref) return 'completed';
  if (ref < today) return 'overdue';
  if (ref <= addDaysToDate(today, APPROACHING_DAYS)) return 'approaching';
  return isDone(v) ? 'completed' : 'upcoming';
};

/** Bugünden takip tarihine kalan takvim günü (negatif = gecikmiş). */
export const getDaysLeft = (v: VaccinationLike, today: string = todayLocal()): number => {
  const ref = trackedDate(v) || normalizeToLocalDate(v.date || '');
  const n = diffInDays(today, ref);
  return Number.isFinite(n) ? n : 0;
};

/** Kalan güne göre kısa etiket; tüm ekranlarda aynı metin. */
export const vaccineDayLabel = (days: number, status: VaccinationStatus): string => {
  if (status === 'completed') return 'Yapıldı';
  if (status === 'overdue') return `${Math.abs(days)} gün gecikti`;
  if (days <= 0) return 'Bugün';
  if (days === 1) return 'Yarın';
  return `${days} gün kaldı`;
};
